import { AxiosError } from 'axios';
import { isNil } from 'lodash';
import { useCallback, useContext, useState } from 'react';

import { RoleNames } from '@bai/utils/types';
import type { Auth, Role, User } from '@bai/utils/types';

import { AuthContext } from './AuthProvider';
import { login, LoginParams, logout } from './AuthProvider.utils';

export function useAuth(): Auth | null {
  return useContext(AuthContext);
}

export function useUser(): User | null {
  const auth = useAuth();
  return auth?.user ?? null;
}

export function useRoles(): Role[] | null {
  const auth = useAuth();
  return auth?.roles ?? null;
}

export function useHasRole(role: RoleNames): boolean {
  const roles = useRoles();
  if (isNil(roles)) return false;
  return roles.some(({ type }) => role.toLowerCase() === type.toLowerCase());
}

export function useLegacyCurrentRole() {
  const auth = useAuth();
  return auth?.legacy_userRole;
}

export function useLegacyHasCurrentRole(role: RoleNames): boolean {
  const legacyRole = useLegacyCurrentRole();
  return legacyRole?.type.toLowerCase() === role.toLowerCase();
}

export function useIsProxied(): boolean {
  const auth = useAuth();
  if (isNil(auth)) return false;
  return auth.impersonate.impersonatorID !== -1;
}

interface UseLoginReturn {
  login: () => Promise<Auth | null>;
  response: Auth | null;
  error: AxiosError | unknown | null;
}

export function useLogin({ username, password }: LoginParams): UseLoginReturn {
  const [res, setRes] = useState<UseLoginReturn['response']>(null);
  const [error, setError] = useState<UseLoginReturn['error']>(null);
  const loginCallback = useCallback(async () => {
    setError(null);
    setRes(null);
    try {
      const response = await login({ username, password });
      setRes(response);
      return response;
    } catch (e) {
      setError(e);
      return null;
    }
  }, [username, password]);
  return { login: loginCallback, response: res, error };
}

interface UseLogoutReturn {
  logout: () => Promise<void | null>;
  response: string | null;
  error: AxiosError | unknown | null;
}

export function useLogout(): UseLogoutReturn {
  const auth = useAuth();
  const [res, setRes] = useState<UseLogoutReturn['response']>(null);
  const [error, setError] = useState<UseLogoutReturn['error']>(null);
  const logoutCallback = useCallback(async () => {
    setError(null);
    setRes(null);
    try {
      await logout(auth?.portalCode);
      return null;
    } catch (e) {
      setError(e);
      return null;
    }
  }, [auth?.portalCode]);
  return { logout: logoutCallback, response: res, error };
}
